<template>
  <bootstrap-modal
    v-show="showModal"
    size="lg"
  >
    <div
      slot="header"
      class="modal-header"
    >
      <button
        type="button"
        class="close modal__close-button"
        data-dismiss="modal"
        aria-label="Close"
        @click="handleCloseClick"
      >
        <span
          aria-hidden="true"
          class="retouching-modal__close-icon"
        >&times;</span>
      </button>
      <h4 class="modal-title">
        <contentful-text :item="contentfulText('crop-modal-title')" /> - {{ aspectRatioName }}
      </h4>
    </div>
    <div
      slot="body"
      class="modal-body"
    >
      <div class="row">
        <div class="col-xs-12 col-sm-6">
          <div class="content crop-modal__content-section">
            <contentful-text
              :item="contentfulText('crop-modal-instructions')"
              :apply-markdown="true"
            />
          </div>
          <div
            v-if="croppedImgPreview"
            class="crop-modal__content-section"
          >
            <contentful-text
              :item="contentfulText('crop-modal-crop-applied-success-notice')"
              :apply-markdown="true"
            />
            <img :src="croppedImgPreview">
          </div>
          <div class="crop__buttons">
            <div class="row">
              <div class="col-xs-offset-1 col-xs-10 col-sm-offset-0 col-sm-10">
                <div
                  v-if="!croppedImgPreview"
                  class="crop-modal__content-section"
                >
                  <button
                    type="button"
                    class="button button--fw crop__button"
                    @click="handleCropPreviewClick"
                  >
                    <contentful-text :item="contentfulText('crop-modal-preview-crop-button-label')" />
                  </button>
                </div>
                <div
                  v-if="croppedImgPreview"
                  class="crop-modal__content-section"
                >
                  <button
                    v-if="croppedImgPreview"
                    type="button"
                    class="button button--fw crop__button crop__undo-button"
                    @click="handleRemoveCropClick"
                  >
                    <contentful-text :item="contentfulText('crop-modal-remove-crop-button-label')" />
                  </button>
                </div>
                <div class="crop-modal__content-section">
                  <button
                    type="button"
                    class="button button--fw crop__button crop__button--outline"
                    @click="$emit('close')"
                  >
                    <contentful-text :item="contentfulText('crop-modal-cancel-button-label')" />
                  </button>
                </div>
                <div
                  v-if="croppedImgPreview"
                  class="crop-modal__content-section"
                >
                  <button
                    type="button"
                    class="button button--fw button--cta crop__button"
                    @click="handleCropSaveClick"
                  >
                    <contentful-text :item="contentfulText('crop-modal-save-button-label')" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xs-offset-1 col-xs-10 col-sm-offset-0 col-sm-6 col-md-offset-1 col-md-5">
          <div
            class="crop__image-wrapper"
          >
            <vue-cropper
              v-if="frame"
              ref="cropper"
              :src="frame.Urls.SmallWatermarked"
              :aspect-ratio="orientedAspectRatio"
              :background="false"
              :guides="false"
              :min-container-height="minContainerHeight"
              :min-container-width="minContainerWidth"
              :min-crop-box-height="minCropBoxHeight"
              :min-crop-box-width="minCropBoxWidth"
              :rotatable="false"
              :view-mode="3"
              :zoomable="false"
              :key="frame.FrameID"
              alt="Source Image"
              drag-mode="none"
            />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 col-sm-6">
          <div class="row">
            <div class="col-xs-12 col-sm-10">
              <button class="crop__save-button button button--fw button--cta">
                <contentful-text :item="contentfulText('crop-modal-save-button-label')" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </bootstrap-modal>
</template>

<script>
import BootstrapModal from '@Shared/components/BootstrapModal'
import ToastMixin from '@Shared/mixins/toast'
import GoogleAnalytics from '@Shared/lib/google-analytics'
import VueCropper from 'vue-cropperjs'
import ContentfulMixin from '@Shared/mixins/contentful'
import ResponsiveBreakpointsMixin from '@Shared/mixins/responsive-breakpoints'

export default {
  name: 'CropModal',
  components: {
    BootstrapModal,
    VueCropper
  },
  mixins: [
    ContentfulMixin,
    ToastMixin,
    ResponsiveBreakpointsMixin
  ],
  props: {
    show: { type: Boolean, default: false },
    frame: { type: Object, default: null },
    aspectRatioName: { type: String, default: null },
    aspectRatio: { type: Number, default: 0 },
    savedCrop: { type: Object, default: null }
  },
  data () {
    return {
      showModal: false,
      croppedImgPreview: null
    }
  },
  computed: {
    orientedAspectRatio () {
      if (this.frame.Orientation === 0) {
        return 1 / this.aspectRatio
      } else {
        return this.aspectRatio
      }
    },
    minContainerHeight () {
      let resizePercentage = this.minContainerWidth / this.frame.OriginalWidthInPixels
      let imageHeightResized = this.frame.OriginalHeightInPixels * resizePercentage

      return imageHeightResized
    },
    minContainerWidth () {
      if (this.isMobile) {
        return 266
      } else if (this.isTablet) {
        return 274
      } else {
        return 350
      }
    },
    minCropBoxHeight () {
      return this.minContainerHeight / 2
    },
    minCropBoxWidth () {
      return this.minContainerWidth / 2
    },
    content () {
      return this.$store.state.catalogPageCommon.cropPhotoModalModuleContent
    }
  },
  watch: {
    show (v, ov) {
      this.showModal = v

      if (v === true) {
        this.setInitialCrop()
      } else {
        this.croppedImgPreview = null
        this.$refs.cropper.reset()
      }
    },
    croppedImgPreview (v, ov) {
      if (v === null) {
        this.$refs.cropper.enable()
      } else {
        this.$refs.cropper.disable()
      }
    }
  },
  methods: {
    handleCloseClick () {
      this.$emit('close')
    },
    setInitialCrop () {
      let cropData = { // everything set as undefined initially has to be set before computation is complete
        height: undefined,
        width: undefined,
        left: 0,
        top: 0
      }

      if (this.savedCrop === null) {
        if (this.frame.Orientation === 0) { // landscape orientation
          cropData.height = this.minContainerHeight
          cropData.width = this.minContainerHeight * this.orientedAspectRatio
          cropData.left = Math.round((this.minContainerWidth - cropData.width) / 2)
        } else { // portrait orientation
          cropData.width = this.minContainerWidth
          cropData.height = this.minContainerHeight * this.orientedAspectRatio
        }
      } else {
        let imageData = this.$refs.cropper.getImageData()

        let x1Px = imageData.width * this.savedCrop.x1Percent
        let x2Px = imageData.width * this.savedCrop.x2Percent
        let y1Px = imageData.height * this.savedCrop.y1Percent
        let y2Px = imageData.height * this.savedCrop.y2Percent

        cropData.top = Math.round(y1Px)
        cropData.left = Math.round(x1Px)
        cropData.width = Math.round(x2Px - x1Px)
        cropData.height = Math.round(y2Px - y1Px)
      }

      this.$refs.cropper.setCropBoxData(cropData)
    },
    handleCropPreviewClick () {
      this.croppedImgPreview = this.$refs.cropper.getCroppedCanvas().toDataURL()
    },
    handleRemoveCropClick () {
      this.croppedImgPreview = null
    },
    handleCropSaveClick () {
      let orderCropData = this.createOrderCropData()
      let url = `${this.$store.state.catalogPageCommon.siteRoot}Ajax/SaveOrderCrops`

      this.$http.post(url, orderCropData).then((resp) => {
        if (resp.status === 200) {
          this.$store.commit('frameCropped', this.frame.FrameID)
          this.$emit('close')
          this.makeToast('success', this.contentfulText('crop-modal-toast-title').Content, this.contentfulText('crop-modal-toast-message-success').Content)
          GoogleAnalytics.trackEvent('Proof', 'Cropped', this.frame.FrameID)
        } else {
          this.makeToast('error', this.contentfulText('crop-modal-toast-title').Content, this.contentfulText('crop-modal-toast-message-fail').Content)
        }
      }).catch(e => {
        this.makeToast('error', this.contentfulText('crop-modal-toast-title').Content, this.contentfulText('crop-modal-toast-message-fail').Content)
      })
    },
    createOrderCropData () {
      /*
        this entire function is taken from the legacy cropping functionality.
        the only changes are the calls to the cropper plugin to get data, which
        have been updated to pull from the Vue plugin. else all remains the
        same as in `WebResources\js\shared\modules\cropping.js`
      */

      let croppedFrameId = this.frame.FrameID
      let croppedAspectRatioTarget = this.aspectRatioName

      let cropSaveArray = []
      let cropDetailsArray = []
      let cropInfo = {}
      let orderCrops = {}
      let thumbWidth = 0
      let thumbHeight = 0

      let cropboxLeft = 0
      let cropboxTop = 0
      let cropboxRight = 0
      let cropboxBottom = 0
      let targetWidth = 0
      let targetHeight = 0
      let aspectRatioType = 0

      if (croppedAspectRatioTarget === '4x6') {
        targetWidth = 4
        targetHeight = 6
        aspectRatioType = 2
      } else if (croppedAspectRatioTarget === '5x7') {
        targetWidth = 5
        targetHeight = 7
        aspectRatioType = 1
      } else {
        targetWidth = 8
        targetHeight = 10
        aspectRatioType = 0
      }

      let imageData = this.$refs.cropper.getImageData()

      thumbWidth = imageData.width
      thumbHeight = imageData.height

      if (thumbWidth > thumbHeight) {
        // swap the width and height.
        let tmp = targetHeight
        targetHeight = targetWidth
        targetWidth = tmp
      }

      let boxData = this.$refs.cropper.getCropBoxData()

      cropboxLeft = boxData.left
      cropboxTop = boxData.top
      cropboxRight = cropboxLeft + boxData.width
      cropboxBottom = cropboxTop + boxData.height

      orderCrops.x1Percent = (1.0 * cropboxLeft / thumbWidth)
      orderCrops.x2Percent = (1.0 * cropboxRight / thumbWidth)
      orderCrops.y1Percent = (1.0 * cropboxTop / thumbHeight)
      orderCrops.y2Percent = (1.0 * cropboxBottom / thumbHeight)
      orderCrops.TargetHeight = targetHeight
      orderCrops.TargetWidth = targetWidth
      orderCrops.AspectRatioType = aspectRatioType
      orderCrops.AspectRatioTypeString = croppedAspectRatioTarget

      cropDetailsArray.push(orderCrops)

      cropInfo.FrameID = croppedFrameId
      cropInfo.Crops = cropDetailsArray

      cropSaveArray.push(cropInfo)

      return cropSaveArray
    }
  }
}
</script>

<style lang="scss">
.crop-modal {
  &__content-section {
    margin-bottom: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/shared/components/catalog/CropModal/CropModal.vue